import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { X, Mail, Phone, Building2, Calendar, DollarSign, Clock, MapPin, Tag, Trash2, ArrowRight, Info } from 'lucide-react';
import ConvertToClientModal from '../components/admin/ConvertToClientModal';
import { getSelectableStatuses, getStatusConfig, getStatusColor, statusRequiresReason, getStatusReasons } from '../utils/leadStatuses';
import { showToast } from '../utils/toast';
import { formatDateTimeEST } from '../utils/timezone';
import { useRole, PERMISSIONS } from '../context/RoleContext';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function StaffLeadDetailDrawer({ lead, isOpen, onClose, onUpdate, isAdmin, staffUsername }) {
  const { hasPermission } = useRole();
  const [status, setStatus] = useState(lead?.status || 'new');
  const [noteText, setNoteText] = useState('');
  const [notes, setNotes] = useState(lead?.notes || []);
  const [auditLogs, setAuditLogs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [isArchived, setIsArchived] = useState(lead?.is_archived || false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  
  // New state for status changes with reasons
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [statusReason, setStatusReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [showReasonDialog, setShowReasonDialog] = useState(false);
  
  // Strategy call state
  const [showCallForm, setShowCallForm] = useState(false);
  const [callDatetime, setCallDatetime] = useState('');
  const [callTimezone, setCallTimezone] = useState('EST');

  useEffect(() => {
    if (isOpen && lead) {
      fetchLeadDetail();
    }
  }, [isOpen, lead]);

  const fetchLeadDetail = async () => {
    if (!lead) return;
    
    try {
      setLoadingDetail(true);
      // Use admin or staff endpoint based on isAdmin prop
      const endpoint = isAdmin
        ? `${API}/api/admin/leads/${lead.id}`
        : `${API}/api/staff/leads/${lead.id}`;
      
      const response = await fetch(endpoint, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setNotes(data.lead.notes || []);
        setStatus(data.lead.status || 'new');
        setIsArchived(data.lead.is_archived || false);
        setAuditLogs(data.audit_logs || []);
      }
    } catch (error) {
      console.error('Error fetching lead detail:', error);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    // Check if this status requires a reason
    if (statusRequiresReason(newStatus)) {
      setSelectedStatus(newStatus);
      setStatusReason('');
      setCustomReason('');
      setShowReasonDialog(true);
      return;
    }
    
    // Otherwise proceed directly
    await submitStatusChange(newStatus, null);
  };
  
  const submitStatusChange = async (newStatus, reason) => {
    try {
      setSaving(true);
      // Use admin or staff endpoint based on isAdmin prop
      const endpoint = isAdmin 
        ? `${API}/api/admin/leads/${lead.id}/status`
        : `${API}/api/staff/leads/${lead.id}/status`;
      
      const body = { status: newStatus };
      if (reason) {
        body.reason = reason;
      }
      
      const response = await fetch(endpoint, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });
      
      if (response.ok) {
        setStatus(newStatus);
        setShowReasonDialog(false);
        await fetchLeadDetail();
        onUpdate();
      } else {
        const errorData = await response.json().catch(() => ({}));
        showToast({
          title: 'Failed to update status',
          message: errorData.detail || 'Unknown error',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error updating status:', error);
      showToast({
        title: 'Error',
        message: 'Failed to update status',
        type: 'error'
      });
    } finally {
      setSaving(false);
    }
  };
  
  const handleReasonSubmit = () => {
    const finalReason = statusReason === 'Other' ? customReason : statusReason;
    if (!finalReason || finalReason.trim() === '') {
      showToast({
        title: 'Reason required',
        message: 'Please select or enter a reason',
        type: 'error'
      });
      return;
    }
    submitStatusChange(selectedStatus, finalReason);
  };
  
  const handleSaveCallDetails = async () => {
    if (!callDatetime) {
      showToast({
        title: 'Date required',
        message: 'Please select a date and time',
        type: 'error'
      });
      return;
    }
    
    try {
      setSaving(true);
      const endpoint = isAdmin
        ? `${API}/api/admin/leads/${lead.id}/call-details`
        : `${API}/api/staff/leads/${lead.id}/call-details`;
      
      const response = await fetch(endpoint, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          strategy_call_datetime: callDatetime,
          strategy_call_timezone: callTimezone
        })
      });
      
      if (response.ok) {
        setShowCallForm(false);
        await fetchLeadDetail();
        onUpdate();
        showToast({
          title: 'Success',
          message: 'Call details saved successfully',
          type: 'success'
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        showToast({
          title: 'Failed to save',
          message: errorData.detail || 'Unknown error',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error saving call details:', error);
      showToast({
        title: 'Error',
        message: 'Failed to save call details',
        type: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    
    try {
      setSaving(true);
      // Use admin or staff endpoint based on isAdmin prop
      const endpoint = isAdmin
        ? `${API}/api/admin/leads/${lead.id}/notes`
        : `${API}/api/staff/leads/${lead.id}/notes`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ text: noteText })
      });
      
      if (response.ok) {
        const data = await response.json();
        setNotes([...notes, data.note]);
        setNoteText('');
        await fetchLeadDetail();
      } else {
        const errorData = await response.json().catch(() => ({}));
        showToast({
          title: 'Failed to add note',
          message: errorData.detail || 'Unknown error',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error adding note:', error);
      showToast({
        title: 'Error',
        message: 'Failed to add note',
        type: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNote = async (index) => {
    if (!isAdmin) return;
    if (!window.confirm('Are you sure you want to delete this note?')) return;
    
    try {
      const response = await fetch(`${API}/api/admin/leads/${lead.id}/notes/${index}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        const newNotes = notes.filter((_, i) => i !== index);
        setNotes(newNotes);
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      showToast({
        title: 'Error',
        message: 'Failed to delete note',
        type: 'error'
      });
    }
  };

  const handleArchiveToggle = async () => {
    const action = isArchived ? 'unarchive' : 'archive';
    const confirmMsg = isArchived 
      ? 'Are you sure you want to unarchive this lead?' 
      : 'Are you sure you want to archive this lead? It will be hidden from the active list.';
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      setSaving(true);
      // Use admin or staff endpoint based on isAdmin prop
      const endpoint = isAdmin
        ? `${API}/api/admin/leads/${lead.id}/${action}`
        : `${API}/api/staff/leads/${lead.id}/${action}`;
      
      const response = await fetch(endpoint, {
        method: 'PATCH',
        credentials: 'include'
      });
      
      if (response.ok) {
        setIsArchived(!isArchived);
        onUpdate(); // Refresh the list
        onClose(); // Close the drawer
      } else {
        const errorData = await response.json().catch(() => ({}));
        showToast({
          title: `Failed to ${action}`,
          message: errorData.detail || 'Unknown error',
          type: 'error'
        });
      }
    } catch (error) {
      console.error(`Error ${action}ing lead:`, error);
      showToast({
        title: 'Error',
        message: `Failed to ${action} lead`,
        type: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (!isOpen || !lead) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      
      <div className="absolute right-0 top-0 bottom-0 w-full md:max-w-3xl bg-white shadow-xl overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between z-10 shadow-sm">
          <div className="flex-1 min-w-0 pr-4">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 truncate">
              {lead.first_name} {lead.last_name}
            </h2>
            <p className="text-sm text-gray-600 truncate">{lead.company}</p>
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={onClose} className="flex-shrink-0 touch-manipulation">
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="px-4 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-6">
          {/* Status Update & Archive */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Lead Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Current Status with Description */}
              <div>
                <Label className="text-sm text-gray-600 mb-2 block">Current Status</Label>
                <div className={`px-3 py-2 rounded-md mb-2 ${getStatusColor(status)}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-medium capitalize">{getStatusConfig(status).label}</span>
                    <div className="group relative">
                      <Info className="h-4 w-4 cursor-help" />
                      <div className="hidden group-hover:block absolute left-0 top-6 z-20 w-64 bg-gray-900 text-white text-xs rounded p-2 shadow-lg">
                        {getStatusConfig(status).description}
                      </div>
                    </div>
                  </div>
                </div>
                
                <Select value={status} onValueChange={handleStatusChange} disabled={saving || isArchived}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {getSelectableStatuses().map(statusConfig => (
                      <SelectItem key={statusConfig.value} value={statusConfig.value}>
                        <div className="flex items-center gap-2">
                          <span>{statusConfig.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {isArchived && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                  <p className="text-sm text-yellow-800">
                    <strong>Archived:</strong> This lead is archived and hidden from the active list.
                  </p>
                </div>
              )}
              
              <Button
                variant={isArchived ? "default" : "outline"}
                size="sm"
                onClick={handleArchiveToggle}
                disabled={saving}
                className="w-full touch-manipulation"
              >
                {saving ? 'Processing...' : (isArchived ? 'Unarchive Lead' : 'Archive Lead')}
              </Button>

              {/* Convert to Client - Agency Director & CSM only */}
              {hasPermission(PERMISSIONS.CONVERT_LEADS) && status !== 'converted_to_client' && !isArchived && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => setShowConvertModal(true)}
                  disabled={saving}
                  className="w-full touch-manipulation bg-green-600 hover:bg-green-700 text-white"
                >
                  <ArrowRight className="h-4 w-4 mr-2" />
                  Convert to Client
                </Button>
              )}

              {status === 'converted_to_client' && (
                <div className="bg-green-50 border border-green-200 rounded-md p-3">
                  <p className="text-sm text-green-800">
                    <strong>Converted:</strong> This lead has been converted to a client.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Strategy Call Section */}
          {(lead.strategy_call_datetime || showCallForm) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Strategy Call</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {lead.strategy_call_datetime && !showCallForm ? (
                  <div>
                    <div className="flex items-center gap-2 text-sm mb-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span className="font-medium text-gray-900">
                        {formatDateTimeEST(lead.strategy_call_datetime)}
                      </span>
                      <span className="text-gray-600">({lead.strategy_call_timezone})</span>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowCallForm(true)}
                      className="w-full"
                    >
                      Update Call Details
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="call_datetime" className="text-sm mb-2 block">Date & Time</Label>
                      <Input
                        id="call_datetime"
                        type="datetime-local"
                        value={callDatetime}
                        onChange={(e) => setCallDatetime(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="call_timezone" className="text-sm mb-2 block">Timezone</Label>
                      <Select value={callTimezone} onValueChange={setCallTimezone}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="EST">EST (Eastern)</SelectItem>
                          <SelectItem value="CST">CST (Central)</SelectItem>
                          <SelectItem value="MST">MST (Mountain)</SelectItem>
                          <SelectItem value="PST">PST (Pacific)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={handleSaveCallDetails}
                        disabled={saving}
                        className="flex-1"
                      >
                        {saving ? 'Saving...' : 'Save Call Details'}
                      </Button>
                      {showCallForm && lead.strategy_call_datetime && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowCallForm(false)}
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">
                      Status will auto-update to "Call Scheduled" when saved.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          
          {/* Schedule Call Button */}
          {!lead.strategy_call_datetime && !showCallForm && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowCallForm(true)}
              className="w-full"
            >
              <Calendar className="h-4 w-4 mr-2" />
              Schedule Strategy Call
            </Button>
          )}

          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <Mail className="h-4 w-4 text-gray-400" />
                <a href={`mailto:${lead.email}`} className="text-blue-600 hover:underline">
                  {lead.email}
                </a>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Phone className="h-4 w-4 text-gray-400" />
                <a href={`tel:${lead.phone}`} className="text-blue-600 hover:underline">
                  {lead.phone}
                </a>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Building2 className="h-4 w-4 text-gray-400" />
                <span className="text-gray-700">{lead.company}</span>
              </div>
            </CardContent>
          </Card>

          {/* Lead Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Lead Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-gray-500">Service Type</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Tag className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-900 capitalize">{lead.service_type?.replace('_', ' ')}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Budget</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <DollarSign className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-900">{lead.budget}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Timeline</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-900 capitalize">{lead.timeline?.replace('_', ' ')}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Created</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-900">{formatDate(lead.created_at)}</span>
                  </div>
                </div>
              </div>
              
              {lead.message && (
                <div className="mt-4 pt-4 border-t">
                  <Label className="text-xs text-gray-500">Message</Label>
                  <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{lead.message}</p>
                </div>
              )}

              {(lead.utm_source || lead.utm_medium || lead.utm_campaign) && (
                <div className="mt-4 pt-4 border-t">
                  <Label className="text-xs text-gray-500 mb-2 block">UTM Parameters</Label>
                  <div className="flex flex-wrap gap-2">
                    {lead.utm_source && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">Source: {lead.utm_source}</span>
                    )}
                    {lead.utm_medium && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">Medium: {lead.utm_medium}</span>
                    )}
                    {lead.utm_campaign && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">Campaign: {lead.utm_campaign}</span>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Notes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add Note */}
              <div className="space-y-2">
                <Textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Add a note..."
                  rows={3}
                />
                <Button 
                  onClick={handleAddNote} 
                  disabled={!noteText.trim() || saving}
                  size="sm"
                >
                  {saving ? 'Adding...' : 'Add Note'}
                </Button>
              </div>

              {/* Notes List */}
              {notes.length > 0 ? (
                <div className="space-y-3 mt-4">
                  {notes.map((note, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <p className="text-sm text-gray-900 whitespace-pre-wrap">{note.text}</p>
                          <p className="text-xs text-gray-500 mt-2">
                            {note.added_by} • {formatDate(note.added_at)}
                          </p>
                        </div>
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteNote(index)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">No notes yet</p>
              )}
            </CardContent>
          </Card>

          {/* Audit Log (Admin Only) */}
          {isAdmin && auditLogs.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Activity History</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {auditLogs.map((log, index) => (
                    <div key={index} className="text-sm py-2 border-b last:border-0">
                      <div className="flex items-start justify-between">
                        <div>
                          <span className="font-medium text-gray-900">{log.staff_username}</span>
                          <span className="text-gray-600 mx-2">•</span>
                          <span className="text-gray-700">{log.details}</span>
                        </div>
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          {formatDate(log.timestamp)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Convert to Client Modal */}
      {showConvertModal && (
        <ConvertToClientModal
          lead={lead}
          isOpen={showConvertModal}
          onClose={() => setShowConvertModal(false)}
          onSuccess={(clientId) => {
            setShowConvertModal(false);
            showToast({
              title: 'Lead converted!',
              message: `Successfully converted to client. ID: ${clientId}`,
              type: 'success'
            });
            onClose(); // Close the drawer
            onUpdate(); // Refresh the leads list
          }}
        />
      )}
      
      {/* Reason Dialog for Lost/Deciding */}
      {showReasonDialog && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowReasonDialog(false)}></div>
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6 space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {getStatusConfig(selectedStatus)?.label} - Reason Required
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Please select why this lead is {getStatusConfig(selectedStatus)?.label.toLowerCase()}
              </p>
            </div>
            
            <div>
              <Label className="text-sm mb-2 block">Reason</Label>
              <Select value={statusReason} onValueChange={setStatusReason}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a reason..." />
                </SelectTrigger>
                <SelectContent>
                  {getStatusReasons(selectedStatus).map(reason => (
                    <SelectItem key={reason} value={reason}>{reason}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {statusReason === 'Other' && (
              <div>
                <Label className="text-sm mb-2 block">Please specify</Label>
                <Textarea
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  placeholder="Enter custom reason..."
                  rows={3}
                />
              </div>
            )}
            
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowReasonDialog(false)}
                disabled={saving}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleReasonSubmit}
                disabled={saving || !statusReason}
                className="flex-1"
              >
                {saving ? 'Saving...' : 'Confirm'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
